import { useTranslation } from "react-i18next";
import { useParams } from "@tanstack/react-router";
import { DiscoverApp, type UtilityMode } from "@majourneys/discover";
import { majourneysDiscoverConfig } from "../config";
import { PageSeo, SITE_ORIGIN, withBrand } from "../../../shared/seo/page-seo";

/**
 * A Discover section at its own URL.
 *
 * The four rail sections used to be one address and a `?utility=` query, which
 * meant the chat a guest wanted to share had no link to send. Each is a real
 * path now, rendering the same shell with the mode handed in by the route.
 *
 * **Canonical, not a robots block.** Mindtrip blocks their live chat in
 * robots.txt, and the result is a Google entry for `mindtrip.ai › chat` that
 * reads "No information is available for this page" under a title scraped
 * from an unrelated site — an indexed dead end. A live surface has nothing to
 * read until someone uses it, so these point their signal at the landing page
 * that does have something to read, and stay perfectly shareable meanwhile.
 */
export function DiscoverSectionPage({
  mode,
  /** The page that carries the copy for this section, and gets the index entry. */
  canonicalOf,
  titleKey,
  descriptionKey,
}: {
  mode: UtilityMode;
  canonicalOf: string;
  titleKey: string;
  descriptionKey: string;
}) {
  const { t } = useTranslation();
  const params = useParams({ strict: false }) as { lang?: string };
  const lang = params.lang ?? "en";
  return (
    <>
      <PageSeo
        title={withBrand(t(titleKey))}
        description={t(descriptionKey)}
        canonicalPath={`/${lang}/${canonicalOf}`}
        lang={lang}
        ogType="website"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${SITE_ORIGIN}/${lang}/${canonicalOf}#page`,
            url: `${SITE_ORIGIN}/${lang}/${canonicalOf}`,
            inLanguage: lang,
            isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
          },
        ]}
      />
      <DiscoverApp config={majourneysDiscoverConfig} initialMode={mode} />
    </>
  );
}
